import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, queryGet, queryPost } from '@/utils';

export const generalApi = createApi({
  reducerPath: 'generalApi',
  baseQuery,
  endpoints: (builder) => ({
    getCriteriaTypes: builder.query({
      query: () => queryGet('/general/code/string-criteria-types'),
    }),
    getCounties: builder.query({
      query: () => queryGet('/general/counties/IN'),
    }),
    getHomeStudyStatus: builder.query({
      query: () => queryGet('/general/code/home-study-status-types'),
    }),
    getHomeStudyTypes: builder.query({
      query: () => queryGet('/general/code/home-study-types'),
    }),
    getAgencies: builder.query({
      query: () => queryGet('/general/code/agencies'),
    }),
    getChildStatusTypes: builder.query({
      query: () => queryGet('/general/code/child-status-types'),
    }),
    getChildStatusReasonTypes: builder.query({
      query: () => queryGet('/general/code/child-status-reason-types'),
    }),
    getNameSuffixes: builder.query({
      query: () => queryGet('/general/code/name-suffixes'),
    }),
    getRaces: builder.query({
      query: () => queryGet('/general/code/race'),
    }),
    getStates: builder.query({
      query: () => queryGet('/general/code/states'),
    }),
    getPhoneTypes: builder.query({
      query: () => queryGet('/general/code/phone-types'),
    }),
    getLeadSources: builder.query({
      query: () => queryGet('/general/code/lead-sources'),
    }),
    getContactTypes: builder.query({
      query: () => queryGet('/general/code/initial-contact-type'),
    }),
    getLeadInquiryReasons: builder.query({
      query: () => queryGet('/general/code/lead-inquiry-reasons'),
    }),
    getGenders: builder.query({
      query: () => queryGet('/general/code/gender'),
    }),
    getSiblingTypes: builder.query({
      query: () => queryGet('/general/code/desire-sibling-types'),
    }),
    getInquiryCompletedReasons: builder.query({
      query: () => queryGet('/general/code/inquiry-completed-reasons'),
    }),
    getFosterCareLicenseTypes: builder.query({
      query: () => queryGet('/general/code/foster-care-license-types'),
    }),
    getChildTaskTypes: builder.query({
      query: () => queryGet('/general/code/child-task-types'),
    }),
    getChildTaskStatus: builder.query({
      query: () => queryGet('/general/code/child-task-status-types'),
    }),
    getChildActivityTypes: builder.query({
      query: () => queryGet('/general/code/child-activity-types'),
    }),
    getChildDocumentTypes: builder.query({
      query: () => queryGet('/general/code/child-document-types'),
    }),
    getParentDocumentTypes: builder.query({
      query: () => queryGet('/general/code/parent-document-types'),
    }),
    getRecommendationTypes: builder.query({
      query: () => queryGet('/general/code/recommendation-types'),
    }),
    getCountryAdoptionLiaisonFamily: builder.mutation({
      query: ({ countryId }) => queryGet(`/general/counties/${countryId}/adoption-liaison-family`),
    }),
    getAdoptionLiaisonsFamily: builder.query({
      query: () => queryGet('/people/adoption-liaisons/select-family'),
    }),
    getAdoptionLiaisonsChild: builder.query({
      query: () => queryGet('/people/adoption-liaisons/select-child'),
    }),
    getWWKRecruiters: builder.query({
      query: () => queryGet('/people/wwk-recruiters/select'),
    }),
    getCaseWorkers: builder.query({
      query: () => queryGet('/people/case-workers/select'),
    }),
    addCaseWorkers: builder.mutation({
      query: (body) => queryPost(`/people/case-workers`, body),
    }),
    addFCMS: builder.mutation({
      query: (body) => queryPost(`/people/fcm-supervisors`, body),
    }),
    getLetters: builder.query({
      query: (params) => queryGet('/letters/inquiry/general', { ...params }),
    }),
    getLettersRecommend: builder.query({
      query: () => queryGet('/letters/recommended'),
    }),
    getPhoto: builder.query({
      query: ({ photoId, ...params }) => queryGet(`blobs/${photoId}/photo/thumbnail/base64`, params),
    }),
    getCodeHomeStudy: builder.query({
      query: ({ statusKey }) => queryGet(`/general/code/parent-status-types/${statusKey}`),
    }),
    getCodeOpenInquiries: builder.query({
      query: ({ inquiryStatusKey }) => queryGet(`/general/code/inquiry-sub-types/${inquiryStatusKey}`),
    }),
    getCodeChildStatus: builder.query({
      query: ({ statusKey }) => queryGet(`/general/code/child-status-types/${statusKey}`),
    }),
    getNoteTypes: builder.query({
      query: () => queryGet('/general/code/general'),
    }),
    getAddressLookup: builder.mutation({
      query: (params) => queryGet('/general/address/lookup', params),
    }),
    getRegionalRecruitmentCoordinator: builder.query({
      query: () => queryGet('/people/regional-recruitment-coordinators/select'),
    }),
    getFCMSupervisor: builder.query({
      query: () => queryGet('/people/fcm-supervisors/select'),
    })
  }),
});

export const {
  useGetCriteriaTypesQuery,
  useGetCountiesQuery,
  useGetHomeStudyStatusQuery,
  useGetHomeStudyTypesQuery,
  useGetAgenciesQuery,
  useGetChildStatusTypesQuery,
  useGetChildStatusReasonTypesQuery,
  useGetNameSuffixesQuery,
  useGetRacesQuery,
  useGetStatesQuery,
  useGetPhoneTypesQuery,
  useGetLeadSourcesQuery,
  useGetContactTypesQuery,
  useGetLeadInquiryReasonsQuery,
  useGetGendersQuery,
  useGetSiblingTypesQuery,
  useGetInquiryCompletedReasonsQuery,
  useGetFosterCareLicenseTypesQuery,
  useGetChildTaskTypesQuery,
  useGetChildTaskStatusQuery,
  useGetChildActivityTypesQuery,
  useGetChildDocumentTypesQuery,
  useGetParentDocumentTypesQuery,
  useGetRecommendationTypesQuery,
  useGetCountryAdoptionLiaisonFamilyMutation,
  useGetAdoptionLiaisonsFamilyQuery,
  useGetAdoptionLiaisonsChildQuery,
  useGetWWKRecruitersQuery,
  useGetCaseWorkersQuery,
  useAddCaseWorkersMutation,
  useAddFCMSMutation,
  useGetLettersQuery,
  useGetLettersRecommendQuery,
  useGetPhotoQuery,
  useGetCodeHomeStudyQuery,
  useGetCodeOpenInquiriesQuery,
  useGetCodeChildStatusQuery,
  useGetNoteTypesQuery,
  useGetAddressLookupMutation,
  useGetRegionalRecruitmentCoordinatorQuery,
  useGetFCMSupervisorQuery,
} = generalApi;
